/**
 * miroImportMapping — Field Mappings sets applied to Miro stickies.
 *
 * Every place items come in from Miro uses this — Items list → Import (template
 * and simple mode) and the Item Pool Editor's integration panel: read a
 * sticky's values, run the active import set over them (fieldMappingApply), set
 * item fields on create, then write attribute values once the item exists, as
 * accepted values that carry their provenance in plain words. Each created
 * item also remembers what its sticky said (item_source_attributes), so a set
 * can be applied to it later. Export runs the export set backwards so a
 * sticky's color comes back from the item's value.
 */

import {
  CONNECTOR_FIELDS,
  MIRO_STICKY_PALETTE,
  loadFieldMappingConfig,
  type FieldMappingConfig,
  type FieldMappingEntry,
  type FieldMappingSet,
} from './fieldMappingRegistry';
import {
  acceptMappedAttribute,
  applyMappingSet,
  attributeKeyOfTarget,
  countMissingAttribute,
  countSkippedFields,
  countUnmatched,
  describeMappingProblems,
  exportThroughMappingSet,
  itemCreateFieldsFromMapping,
  itemValueForTarget,
  mappingValueNote,
  newMappingProblems,
  plural,
  resolveActiveMappingSet,
  ruleRunsOn,
  type AcceptDirectly,
  type ActiveMappingSet,
  type MappedAttributeValue,
  type MappedValue,
  type SkippedFieldReason,
  type UnmatchedValue,
} from './fieldMappingApply';
import {
  recordSourceAttributes,
  type SourceAttributeInput,
} from './semanticConnectors/unifiedAttributesService';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import { useUserStore } from '../stores/prioritization/userStore';

// ── One sticky → external values ──────────────────────────────────────────────

/**
 * A sticky as either Miro import endpoint returns it
 * (engines/integrations-engine/src/routes/miro.ts):
 *   simple mode   /miro/import            { label, description?, externalId, attributes: { color, author, board_name, tags? } }
 *   template mode /miro/templates/import  { id, content, style: { fillColor }, zone: { name }, rawMetadata: { frameTitle? }, author, tags? }
 * `tags` are the sticky's Miro tag titles; absent when the engine could not read the board's tags.
 */
export interface MiroStickyRecord {
  id?: string;
  externalId?: string;
  content?: string;
  label?: string;
  title?: string;
  description?: string;
  color?: string;
  author?: string;
  tags?: string[];
  style?: { fillColor?: string };
  zone?: { name?: string | null } | null;
  rawMetadata?: { frameTitle?: string } & Record<string, unknown>;
  attributes?: { color?: string; author?: string; board_name?: string; tags?: string[] } & Record<string, unknown>;
}

const STICKY_COLOR_VALUES: ReadonlySet<string> = new Set(
  CONNECTOR_FIELDS.miro.find((f) => f.key === 'sticky_color')?.enumValues?.map((v) => v.value) ?? [],
);

/**
 * A Miro fill color as a Sticky Note Color value. Miro names its purple
 * "violet"; its light/dark shades that the list has no entry for count as
 * the base color (light_blue → blue). Anything else passes through unchanged.
 */
export function miroStickyColorValue(fillColor: string | undefined): string | undefined {
  if (!fillColor) return undefined;
  const c = fillColor.toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (!c) return undefined;
  if (STICKY_COLOR_VALUES.has(c)) return c;
  if (c === 'violet') return 'purple';
  const base = c.replace(/^(light|dark)_/, '');
  return STICKY_COLOR_VALUES.has(base) ? base : c;
}

/** A Sticky Note Color value as a Miro fill color, or undefined when Miro has no such color. */
export function miroFillColorFor(stickyColor: unknown): string | undefined {
  const c = String(stickyColor ?? '').toLowerCase().trim();
  if (c === 'purple') return 'violet';
  return MIRO_STICKY_PALETTE.some((p) => p.name === c) ? c : undefined;
}

/** The engine's placeholder when a sticky has no creator name. */
const UNKNOWN_AUTHOR = 'Unknown';

/**
 * One sticky's values keyed by the Miro field keys on Field Mappings
 * (CONNECTOR_FIELDS.miro). `boardName` comes from the caller in template mode,
 * which does not return it. Frame / Container is the frame the sticky sits in,
 * else its template zone.
 */
export function miroStickyExternalValues(
  record: MiroStickyRecord,
  context: { boardName?: string } = {},
): Record<string, unknown> {
  // Simple mode truncates `label` to 100 characters and keeps the full text in
  // `description` only when it is longer.
  const text = record.content ?? record.description ?? record.label ?? record.title;
  const author = record.attributes?.author ?? record.author;
  const tags = (record.attributes?.tags ?? record.tags ?? []).map((t) => String(t).trim()).filter(Boolean);
  return {
    sticky_color: miroStickyColorValue(record.attributes?.color ?? record.style?.fillColor ?? record.color),
    sticky_text: text?.trim() || undefined,
    frame_name: record.rawMetadata?.frameTitle ?? record.zone?.name ?? undefined,
    tag: tags.length > 0 ? tags : undefined,
    board_name: record.attributes?.board_name ?? context.boardName,
    created_by: author && author !== UNKNOWN_AUTHOR ? author : undefined,
  };
}

/** The sticky's own Miro id (simple mode prefixes `id` with "miro-"; `externalId` is the raw id). */
export function miroStickyId(record: MiroStickyRecord): string | null {
  return record.externalId ?? record.id ?? null;
}

/**
 * What to remember about one sticky's item: one row per value it had, keyed by
 * the Miro field keys on Field Mappings (tags as a list).
 */
export function miroStickySourceRows(
  itemId: string,
  externalId: string | null,
  values: Record<string, unknown>,
): SourceAttributeInput[] {
  return Object.entries(values)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([sourceKey, rawValue]) => ({ itemId, externalId, sourceKind: 'field' as const, sourceKey, rawValue }));
}

// ── An import run ─────────────────────────────────────────────────────────────

export interface MiroMappedSticky {
  /** Spread onto the item create call (title, description, itemCategory, altitudeLevel, strategicBucket). */
  itemFields: Record<string, string>;
  /** Tags to add to the item. */
  tags: string[];
  /** Written as accepted values once the item exists. */
  attributes: MappedAttributeValue[];
  unmatched: UnmatchedValue[];
  skippedFields: Array<{ mapped: MappedValue; reason: SkippedFieldReason }>;
  /** What the sticky said — remembered once its item exists, whether or not a set applied. */
  source: { externalId: string | null; values: Record<string, unknown> };
}

export interface MappingWriteDeps {
  /** Keys of the workspace's own attributes; values for any other key are skipped. */
  attributeKeys: ReadonlySet<string>;
  /** Who the values are accepted by — the person running the import. */
  acceptedBy: string | null;
  accept: AcceptDirectly;
  /** Remembers what each sticky said (recordSourceAttributes); omitted when there is no workspace. */
  record?: (rows: SourceAttributeInput[]) => Promise<{ recorded: number; error?: string }>;
}

export interface MappingImportSummary {
  message: string;
  /** True when something was skipped or could not be saved. */
  hasProblems: boolean;
}

export interface MiroImportMapping {
  active: ActiveMappingSet;
  map(record: MiroStickyRecord): MiroMappedSticky;
  /** Call once a sticky's item is saved: counts its fields, writes its attribute values, remembers what the sticky said. */
  finish(itemId: string, mapped: MiroMappedSticky): Promise<void>;
  /** For a host that creates the items: `savedIds[i]` is the item saved for `mapped[i]`. */
  finishSaved(mapped: MiroMappedSticky[], savedIds: ReadonlyArray<string | undefined> | undefined): Promise<void>;
  /** What the set did, in plain words; null when no set applied and nothing went wrong. */
  summary(): MappingImportSummary | null;
}

/** The note on a value an import writes; starts with MAPPING_NOTE_PREFIX like every mapping-written value. */
export const MIRO_IMPORT_NOTE_WHEN = 'at Miro import';

export function createMiroImportMapping(
  active: ActiveMappingSet,
  deps: MappingWriteDeps,
  context: { boardName?: string } = {},
): MiroImportMapping {
  const set: FieldMappingSet | null = active.set;
  let valuesSet = 0;
  const problems = newMappingProblems();
  let notRecorded = 0;
  let firstRecordError = '';

  const run: MiroImportMapping = {
    active,

    map(record) {
      const values = miroStickyExternalValues(record, context);
      const source = { externalId: miroStickyId(record), values };
      if (!set) return { itemFields: {}, tags: [], attributes: [], unmatched: [], skippedFields: [], source };
      const applied = applyMappingSet(values, set);
      const item = itemCreateFieldsFromMapping(applied.fields);
      return {
        itemFields: item.fields,
        tags: item.tags,
        attributes: applied.attributes,
        unmatched: applied.unmatched,
        skippedFields: item.skipped,
        source,
      };
    },

    async finish(itemId, mapped) {
      valuesSet += Object.keys(mapped.itemFields).length + mapped.tags.length;
      countUnmatched(problems, 'miro', mapped.unmatched);
      countSkippedFields(problems, mapped.skippedFields);
      for (const a of mapped.attributes) {
        if (!deps.attributeKeys.has(a.attributeKey)) {
          countMissingAttribute(problems, a.attributeKey);
          continue;
        }
        const note = mappingValueNote(MIRO_IMPORT_NOTE_WHEN, 'miro', a, set?.name ?? '');
        if (await acceptMappedAttribute(deps, itemId, a, note, problems)) valuesSet++;
      }
      if (deps.record) {
        const rows = miroStickySourceRows(itemId, mapped.source.externalId, mapped.source.values);
        const result = await deps.record(rows);
        if (result.error) {
          notRecorded++;
          firstRecordError ||= result.error;
        }
      }
    },

    async finishSaved(mapped, savedIds) {
      for (const [i, m] of mapped.entries()) {
        const itemId = savedIds?.[i];
        if (itemId) await run.finish(itemId, m);
      }
    },

    summary() {
      const parts: string[] = [];
      let hasProblems = false;
      if (set) {
        parts.push(`Mapping set “${set.name}”: ${valuesSet > 0 ? `${plural(valuesSet, 'value')} set` : 'no values set'}.`);
        const described = describeMappingProblems(problems);
        parts.push(...described.sentences);
        hasProblems = described.hasProblems;
      }
      if (notRecorded > 0) {
        const reason = firstRecordError.trim().replace(/\.$/, '');
        parts.push(
          `What ${plural(notRecorded, 'sticky', 'stickies')} said was not saved, so a mapping set can't be applied to ${notRecorded === 1 ? 'its item' : 'their items'} later: ${reason}.`,
        );
        hasProblems = true;
      }
      return parts.length > 0 ? { message: parts.join(' '), hasProblems } : null;
    },
  };
  return run;
}

/** The workspace's own (scoring) attribute keys, loading the store first if it never loaded. */
export async function workspaceAttributeKeys(): Promise<Set<string>> {
  if (!useAttributesStore.getState().syncState.lastSyncAt) {
    await useAttributesStore.getState().fetchFromSupabase();
  }
  return new Set(
    useAttributesStore.getState().attributes.filter((a) => !a.isCustomField).map((a) => a.key),
  );
}

/** Load the Miro set that applies on import and everything needed to write its values. */
export async function prepareMiroImportMapping(
  workspaceId: string | null,
  context: { boardName?: string } = {},
): Promise<MiroImportMapping> {
  const active: ActiveMappingSet = workspaceId
    ? resolveActiveMappingSet(await loadFieldMappingConfig(workspaceId, 'miro'), 'import')
    : { set: null, chosen: false };
  const needsAttributes = !!active.set?.entries.some(
    (e) => ruleRunsOn(e, 'import') && attributeKeyOfTarget(e.internalFieldKey),
  );
  return createMiroImportMapping(
    active,
    {
      attributeKeys: needsAttributes ? await workspaceAttributeKeys() : new Set(),
      acceptedBy: useUserStore.getState().userId || null,
      accept: (params) => useValueProposalStore.getState().acceptValueDirectly(params),
      // Remembered whether or not a set applied, so one can be applied later.
      record: workspaceId
        ? (rows) => recordSourceAttributes({ workspaceId, connectorType: 'miro', origin: 'import', rows })
        : undefined,
    },
    context,
  );
}

// ── Export: item values → sticky colors ───────────────────────────────────────

/**
 * Sticky colors for an export, from the export set run backwards: an item's
 * accepted value (or its own field) goes back through the rule's value list to
 * a Sticky Note Color. Items with no match get no color (Miro's default).
 */
export function miroStickyColorsFromSet(
  set: FieldMappingSet | null,
  records: ReadonlyArray<{ id: string }>,
  acceptedValueOf: (itemId: string, attributeKey: string) => unknown,
): Map<string, string> {
  const colors = new Map<string, string>();
  const rules = (set?.entries ?? []).filter((e) => ruleRunsOn(e, 'export') && e.externalFieldKey === 'sticky_color');
  if (rules.length === 0) return colors;
  for (const record of records) {
    const internal: Record<string, unknown> = {};
    for (const rule of rules) {
      const attributeKey = attributeKeyOfTarget(rule.internalFieldKey);
      internal[rule.internalFieldKey] = attributeKey
        ? acceptedValueOf(record.id, attributeKey)
        : itemValueForTarget(record as Record<string, unknown>, rule.internalFieldKey);
    }
    const color = miroFillColorFor(exportThroughMappingSet(internal, set).sticky_color?.value);
    if (color) colors.set(record.id, color);
  }
  return colors;
}

const isColorRule = (e: FieldMappingEntry, flow: 'import' | 'export'): boolean =>
  ruleRunsOn(e, flow) && e.externalFieldKey === 'sticky_color';

/**
 * The set that colors stickies on the way back out. The export set, when it
 * has a color rule. Otherwise the import set's color rules read backwards: a
 * rule saved as "Import only" still says what each color means, and what came
 * in as yellow should go back as yellow ("it comes back as it was").
 */
export function roundTripExportSet(config: FieldMappingConfig): ActiveMappingSet {
  const exportSet = resolveActiveMappingSet(config, 'export');
  if (exportSet.set?.entries.some((e) => isColorRule(e, 'export'))) return exportSet;
  const importSet = resolveActiveMappingSet(config, 'import');
  const colorRules = (importSet.set?.entries ?? []).filter((e) => isColorRule(e, 'import') && e.valueMap?.length);
  if (!importSet.set || colorRules.length === 0) return exportSet;
  return {
    set: { ...importSet.set, entries: colorRules.map((e) => ({ ...e, direction: 'both' as const })) },
    chosen: importSet.chosen,
  };
}

/** Load the Miro export set and the accepted values it reads, then color each record. */
export async function miroStickyColorsForExport(
  workspaceId: string | null,
  records: ReadonlyArray<{ id: string }>,
): Promise<{ colors: Map<string, string>; active: ActiveMappingSet }> {
  if (!workspaceId) return { colors: new Map(), active: { set: null, chosen: false } };
  const active = roundTripExportSet(await loadFieldMappingConfig(workspaceId, 'miro'));
  const readsValues = !!active.set?.entries.some(
    (e) => ruleRunsOn(e, 'export') && e.externalFieldKey === 'sticky_color' && attributeKeyOfTarget(e.internalFieldKey),
  );
  if (readsValues && !useValueProposalStore.getState().syncState.lastSyncAt) {
    await useValueProposalStore.getState().fetchFromSupabase();
  }
  const values = useValueProposalStore.getState();
  const colors = miroStickyColorsFromSet(
    active.set,
    records,
    (itemId, attributeKey) => values.getAcceptedValue(itemId, attributeKey)?.value,
  );
  return { colors, active };
}
